import { Button, Collapse, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PropsWithChildren } from "react";
import { useFormikContext } from "formik";
import dayjs from "dayjs";
import { EXCHANGE_TIMEZONE } from "../../../dayjsSetup";
import { ConfigTabState } from "../types";
import { tradeEndBeforeStart } from "./time/utils";
import { balancingGroupLabel } from "../balancingGroup";

/**
 * The trade window and balancing group, folded away by default so the order row stays one line high
 * and the depth ladder below keeps its room.
 */
export const Advanced = (props: PropsWithChildren<{ open: boolean }>) => (
  <Collapse in={props.open} unmountOnExit>
    <Stack
      spacing={1}
      direction="row"
      sx={{ alignItems: "stretch", flexWrap: "wrap", rowGap: 1, paddingTop: 1 }}
    >
      {props.children}
    </Stack>
  </Collapse>
);

const jst = (time: dayjs.Dayjs) =>
  dayjs(time).tz(EXCHANGE_TIMEZONE).format("HH:mm");

/**
 * Opens {@link Advanced}, and summarises what is in it while it is shut.
 *
 * The summary is not decoration: an orderbook prefill sets trade end to an hour
 * before delivery, and an edited algo brings its own window, so a hidden window
 * is usually a *set* one. The balancing group is always set, and carries over
 * from the last algo, so it is shown for the same reason. It also turns red on the end-before-start error that
 * disables Submit, which would otherwise be a disabled button with no visible
 * reason.
 */
export const AdvancedToggle = (props: {
  open: boolean;
  onToggle: () => void;
}) => {
  const { values } = useFormikContext<ConfigTabState>();
  const error = tradeEndBeforeStart(values.start, values.end) ?? false;

  const start = values.start ? jst(values.start) : "now";
  const end = values.end ? jst(values.end) : "close";

  return (
    <Button
      size="small"
      color={error ? "error" : "inherit"}
      onClick={props.onToggle}
      aria-expanded={props.open}
      startIcon={
        <ExpandMoreIcon
          sx={{
            transform: props.open ? "rotate(180deg)" : undefined,
            transition: "transform 150ms",
          }}
        />
      }
      sx={{ textTransform: "none", minWidth: 0, gap: 1 }}
    >
      Advanced
      <Typography
        component="span"
        sx={{
          fontSize: 11,
          color: error ? "error.main" : "text.secondary",
          whiteSpace: "nowrap",
        }}
      >
        {start} → {end} JST
        {values.balancing_group
          ? ` · ${balancingGroupLabel[values.balancing_group]}`
          : null}
      </Typography>
    </Button>
  );
};
